import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueryClient, type Query } from '@tanstack/react-query';
import type { AgentIterationDataError, MonitoringDataError, Run } from '@/types/monitoring';
import type { RequestTriggerDetails } from '@/services/monitoringApi';
import { RUN_SCOPED_QUERY_KEY_PREFIXES } from './useMonitoringData';
import { telemetryService } from '@/services/telemetryService';

const summaries = new Set(['agentIterations', 'runTimeline', 'chatHistory', 'actionChatHistory']);
const copiedDetails = new Set(['agentIterationActions', 'actionRepetitions', 'repetitionScopeActions', 'chatMessageIterationNodeExecutions']);

function describeDataError(key: readonly unknown[]): MonitoringDataError {
  const actionNodeId = typeof key[2] === 'string' ? `action-${key[2]}` : undefined;
  const iterationName = typeof key[3] === 'string' ? key[3] : undefined;
  switch (key[0]) {
    case 'actionDetails':
      return { message: 'Action details unavailable', nodeId: typeof key[2] === 'string' ? key[2] : undefined };
    case 'actionRepetitions':
    case 'childActionRepetitions':
    case 'agentIterations':
      return { message: 'Iterations unavailable', nodeId: actionNodeId };
    case 'repetitionDetails':
    case 'agentIterationDetails':
      return { message: 'Iteration details unavailable', nodeId: actionNodeId, iterationName };
    case 'repetitionScopeActions':
      return { message: 'Iteration actions unavailable', nodeId: actionNodeId, iterationName };
    case 'actionChatHistory':
      return { message: 'Conversation unavailable', nodeId: actionNodeId };
    case 'chatHistory':
      return { message: 'Conversation unavailable' };
    case 'runTimeline':
      return { message: 'Timeline unavailable' };
    case 'chatMessageIterationActions':
    case 'chatMessageIterationNodeExecutions':
      return { message: 'Iteration actions unavailable', messageIndex: typeof key[2] === 'number' ? key[2] : undefined };
    default:
      return { message: 'Run details unavailable' };
  }
}

/** Refresh only observed queries and the lazy details already loaded into the log. */
export function isRefreshableMonitoringQuery(query: Query, runId: string) {
  const prefix = String(query.queryKey[0]);
  if (query.queryKey[1] !== runId || !RUN_SCOPED_QUERY_KEY_PREFIXES.has(prefix)) return false;
  if (prefix === 'actionDetails' && query.queryKey.at(-1) === 'request'
    && String(query.queryKey[2]).startsWith('trigger-')) {
    // Failed/protected Request content owns its per-side retry policy.
    const data = query.state.data as RequestTriggerDetails | undefined;
    if (!data?.requestContent || Object.values(data.requestContent)
      .some(side => side.status === 'error' || side.status === 'secured')) return false;
  }
  return query.isActive() || (copiedDetails.has(prefix) && query.getObserversCount() === 0);
}

export function useMonitoringRefresh(
  runId: string | null,
  status: Run['status'] | undefined,
  enabled: boolean,
  onRefreshed: () => void,
) {
  const client = useQueryClient();
  const latest = useRef({ runId, enabled, onRefreshed });
  latest.current = { runId, enabled, onRefreshed };
  const deadlines = useRef(new Map<string, number>());
  const pending = useRef<{ runId: string; promise: Promise<void> } | null>(null);
  const cache = client.getQueryCache();
  const subscribe = useCallback((notify: () => void) => cache.subscribe(notify), [cache]);
  const getRelevantQueries = useCallback(() => {
    if (!runId) return [];
    const relevant = new Map<string, Query>();
    for (const query of cache.findAll({ predicate: query => isRefreshableMonitoringQuery(query, runId) })) {
      // Later lazy-key variants supersede earlier ones (tool count / scope children).
      const identity = copiedDetails.has(String(query.queryKey[0])) ? query.queryKey.slice(0, 5) : query.queryKey;
      relevant.set(JSON.stringify(identity), query);
    }
    return Array.from(relevant.values());
  }, [cache, runId]);
  const errorSnapshot = useRef<{ dataErrors: MonitoringDataError[]; toolErrors: AgentIterationDataError[] }>({
    dataErrors: [], toolErrors: [],
  });
  const getErrors = useCallback(() => {
    const dataErrors: MonitoringDataError[] = [];
    const toolErrors: AgentIterationDataError[] = [];
    for (const { state, queryKey } of enabled ? getRelevantQueries() : []) {
      // A failed first load becomes pending during retry, before any recovery.
      if (state.status !== 'error' && !(state.status === 'pending' && state.errorUpdateCount > 0)) continue;
      if (queryKey[0] === 'agentIterationActions'
        && typeof queryKey[2] === 'string' && typeof queryKey[3] === 'string') {
        toolErrors.push({ agentName: queryKey[2], iterationName: queryKey[3] });
      } else {
        const error = describeDataError(queryKey);
        if (!dataErrors.some(previous => JSON.stringify(previous) === JSON.stringify(error))) dataErrors.push(error);
      }
    }
    // useSyncExternalStore requires a stable snapshot when the projection is unchanged.
    const previous = errorSnapshot.current;
    if (JSON.stringify(previous.dataErrors) !== JSON.stringify(dataErrors) || previous.toolErrors.length !== toolErrors.length
      || toolErrors.some((error, index) => error.agentName !== previous.toolErrors[index]?.agentName
        || error.iterationName !== previous.toolErrors[index]?.iterationName)) {
      errorSnapshot.current = { dataErrors, toolErrors };
    }
    return errorSnapshot.current;
  }, [enabled, getRelevantQueries]);
  const { dataErrors, toolErrors } = useSyncExternalStore(subscribe, getErrors);
  const terminal = status !== undefined && ['succeeded', 'failed', 'cancelled'].includes(status);
  if (runId && terminal && !deadlines.current.has(runId)) {
    deadlines.current.set(runId, Date.now() + 30_000);
  }

  const refresh = useCallback(() => {
    if (!runId || !latest.current.enabled) return Promise.resolve();
    if (pending.current?.runId === runId) return pending.current.promise;
    const current = () => latest.current.enabled && latest.current.runId === runId;
    const refetch = async (predicate: (query: Query) => boolean) => {
      const queries = getRelevantQueries().filter(predicate);
      // Wait for every sibling before copying results, including when one fails.
      await client.refetchQueries({ predicate: query => queries.includes(query) }, { cancelRefetch: false });
      const failure = queries.find(query => query.state.status === 'error');
      if (failure) throw failure.state.error;
    };
    const promise = (async () => {
      try {
        await refetch(query => query.queryKey[0] === 'run');
        if (!current()) return;
        // Tool content links can change without the iteration count changing.
        await refetch(query => summaries.has(String(query.queryKey[0])));
        if (!current()) return;
        await refetch(query => query.queryKey[0] !== 'run' && !summaries.has(String(query.queryKey[0])));
      } finally {
        if (current()) latest.current.onRefreshed();
        if (pending.current?.runId === runId) pending.current = null;
      }
    })();
    pending.current = { runId, promise };
    return promise;
  }, [client, runId, getRelevantQueries]);

  useEffect(() => {
    if (!enabled || !runId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const deadline = deadlines.current.get(runId);
    const schedule = (delay: number) => {
      if (stopped || (deadline !== undefined && Date.now() + delay > deadline)) return;
      timer = setTimeout(async () => {
        try {
          if (deadline !== undefined && Date.now() > deadline) return;
          if (document.visibilityState !== 'hidden') await refresh();
        } catch (error) {
          telemetryService.trackException(error instanceof Error ? error : new Error('Monitoring refresh failed'), {
            context: 'monitoring.run.auto_refresh',
          });
        } finally {
          schedule(3_000);
        }
      }, delay);
    };
    schedule(terminal ? 0 : 3_000);
    return () => { stopped = true; clearTimeout(timer); };
  }, [enabled, runId, terminal, refresh]);

  return { refresh, hasError: dataErrors.length > 0 || toolErrors.length > 0, dataErrors, toolErrors };
}
